import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Modal, Notice } from '../patterns';

interface UserMinimal { _id: string; name: string; username: string; }

interface Props {
  show: boolean;
  user: UserMinimal;
  onHide: () => void;
  onResetComplete?: () => void;
}

const ResetPasswordModal: React.FC<Props> = ({ show, user, onHide, onResetComplete }) => {
  const { t } = useTranslation();
  const [stage, setStage] = useState<'confirm' | 'reveal'>('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleReset = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`/api/v1/users/${user._id}/reset-password`, null, { withCredentials: true });
      setTempPassword(res.data.tempPassword);
      setStage('reveal');
      onResetComplete?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || t('system.users.resetPassword.errorFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleClose = () => {
    setStage('confirm');
    setTempPassword('');
    setError(null);
    setCopied(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} title={t('system.users.resetPassword.title')} size="md">
      <Modal.Body>
        {stage === 'confirm' ? (
          <>
            <p>{t('system.users.resetPassword.confirmBody', { name: user.name, username: user.username })}</p>
            {error && <Notice tone="danger">{error}</Notice>}
          </>
        ) : (
          <>
            <p>{t('system.users.resetPassword.tempPasswordHelp')}</p>
            <div className="pat-field">
              <label className="pat-field__label">{t('system.users.resetPassword.tempPasswordLabel')}</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code data-testid="temp-password" style={{ fontFamily: 'monospace', fontSize: 18, padding: '8px 12px', border: '1px solid var(--shell-border)', borderRadius: 6 }}>
                  {tempPassword}
                </code>
                <button type="button" className="pat-btn pat-btn--subtle" onClick={handleCopy}>
                  {copied ? t('system.users.resetPassword.copied') : t('system.users.resetPassword.copyButton')}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {stage === 'confirm' ? (
          <>
            <button type="button" className="pat-btn pat-btn--subtle" onClick={handleClose} disabled={submitting}>
              {t('system.users.resetPassword.cancel')}
            </button>
            <button type="button" className="pat-btn pat-btn--danger" onClick={handleReset} disabled={submitting}>
              {t('system.users.resetPassword.confirmAction')}
            </button>
          </>
        ) : (
          <button type="button" className="pat-btn pat-btn--primary" onClick={handleClose}>
            {t('system.users.resetPassword.doneAction')}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ResetPasswordModal;
